import React, { useMemo, useState, useEffect } from 'react';
import { Dropdown, Image } from 'semantic-ui-react';
import { proxyGravatarUrl } from '../../../utils/avatarProxy';

const DefaultAvatar = React.memo(() => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="15" cy="15" r="15" fill="var(--muted, #E0E1E2)" />
    <circle cx="15" cy="12" r="5" fill="var(--muted-foreground, #A0A1A2)" />
    <path d="M5 27.5C5 22 9.5 18 15 18C20.5 18 25 22 25 27.5" fill="var(--muted-foreground, #A0A1A2)" />
  </svg>
));

// NOTE: This is the ACTUAL component that renders the user profile menu in the header.
// It handles all user menu interactions including:
// - Profile links (opens in new tab when URL matches /users/{id}/profile)
// - Logout functionality
// - Navigation to other menu items (Dashboard, Settings, etc.)
const UserMenu = React.memo(({ user, userMenu, userMenuChange }) => {
  // Gravatar's redirect for users without a picture has no CORS header, which
  // crossOrigin="anonymous" requires; load it through our same-origin proxy.
  const [currentSrc, setCurrentSrc] = useState(proxyGravatarUrl(user.avatar));
  const [showFallback, setShowFallback] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  useEffect(() => {
    setCurrentSrc(proxyGravatarUrl(user.avatar));
    setShowFallback(false);
  }, [user.avatar]);

  const handleImageError = useMemo(() => (e) => {
    e.target.onerror = null;
    const fallbackAvatar = document.getElementById('avatar');

    if (fallbackAvatar?.src && !showFallback) {
      setShowFallback(true);
      setCurrentSrc(fallbackAvatar.src);
    } else {
      e.target.style.display = 'none';
      const defaultAvatar = e.target.parentElement.querySelector('svg');
      if (defaultAvatar) defaultAvatar.style.display = 'block';
    }
  }, [showFallback]);

  const handleMenuChange = async (e, data) => {
    const selectedOption = options.find(opt => opt.value === data.value);
    if (!selectedOption) return;

    // Close dropdown and force reset by changing key
    setIsOpen(false);
    setResetKey(prev => prev + 1);

    if (selectedOption.value && selectedOption.value.includes('/users/sign_out')) {
      e.preventDefault();
      const form = document.getElementById('logout');
      if (form) form.submit();
    } else if (selectedOption.value && selectedOption.value.match(/\/users\/\d+\/profile/)) {
      // Open profile links in new tab
      window.open(selectedOption.value, '_blank', 'noopener,noreferrer');
    } else if (selectedOption.value) {
      // Navigate directly - beforeunload handler will show warning if needed
      window.location.href = selectedOption.value;
    }
  };

  const trigger = useMemo(() => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      padding: '4px 8px',
      borderRadius: 'var(--radius)',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease'
    }}>
      <span style={{
        display: 'inline-block',
        width: '30px',
        height: '30px',
        position: 'relative'
      }}>
        {!showFallback && (
          <Image
            src={currentSrc}
            avatar
            loading='eager'
            crossOrigin="anonymous"
            referrerPolicy="no-referrer"
            onError={handleImageError}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              margin: 0
            }}
          />
        )}
        {(showFallback) && <DefaultAvatar />}
      </span>
    </div>
  ), [user.avatar, currentSrc, showFallback, handleImageError]);

  const options = useMemo(() => userMenu.map((menu, index) => ({
    key: `menu-item-${index}`,
    text: menu.text,
    value: menu.value,
    disabled: menu.disabled,
    data: {
      originalKey: menu.key
    }
  })), [userMenu]);

  return (
    <>
      <style>{`
        .ui.dropdown {
          padding: 4px 8px;
          border-radius: var(--radius);
        }
        .ui.dropdown:hover {
          background-color: var(--sidebar-accent);
        }
        .ui.dropdown .menu {
          background: var(--popover) !important;
          border: 1px solid var(--border) !important;
          box-shadow: var(--shadow-lg) !important;
        }
        .ui.dropdown .menu .item {
          color: var(--popover-foreground) !important;
        }
        .ui.dropdown .menu .item:hover {
          background: var(--accent) !important;
        }
      `}</style>
      <Dropdown
        key={`profileMenu-${user.id}-${resetKey}`}
        onChange={handleMenuChange}
        onClose={() => setIsOpen(false)}
        onOpen={() => setIsOpen(true)}
        open={isOpen}
        pointing='right'
        style={{
          paddingLeft: 15,
          display: 'flex',
          alignItems: 'center'
        }}
        trigger={trigger}
        closeOnBlur={true}
        selectOnBlur={false}
        options={options}
        selectOnNavigation={false}
      />
    </>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.user?.id === nextProps.user?.id &&
    prevProps.user?.avatar === nextProps.user?.avatar &&
    JSON.stringify(prevProps.userMenu) === JSON.stringify(nextProps.userMenu)
  );
});

export default UserMenu;
